import React, { ReactElement, useEffect } from 'react'
import { config } from '../../config'
import { useEvent } from '../../hooks/analytics/use-event/use-event'
import { useFirebaseToken } from '../../hooks/auth/use-firebase-token'
import { useCampaign } from '../../hooks/campaign/use-campaing/use-campaign'
import { useSidebar } from '../../hooks/sidebar'
import { useToast } from '../../hooks/toast'
import { useGetUserData } from '../../hooks/user/use-get-user-data/use-get-user-data'
import { Moises, MoisesCli } from '../../lib/cli'
import { ShellContext } from '../../types'

const MoisesCLI = Moises({ apiEndpoint: config.api.endpoint })

export const ContextShell = React.createContext<ShellContext<MoisesCli>>({
  user: null,
  userToken: null,
  getValidToken: async () => null,
  refreshToken: async () => {},
  loadingUserData: false,
  silentRetry: async () => {},
  retry: async () => {},
  isFetchingUserData: () => false,
  event: {
    sendEvent: () => {},
    eventTrack: async () => {},
    sendEventPostHog: async () => {},
    rudderstack: {
      identify: () => {},
      track: () => {},
      page: () => {},
      isReady: false
    }
  },
  toast: {
    list: [],
    add: () => {},
    onClearFixedToast: () => {}
  },
  sidebar: {
    isOpenedSidebar: true,
    onCollapsedSidebar: () => {},
    onOpenSidebar: () => {}
  },
  campaign: {
    id: null,
    endDate: null,
    profileBannerWeb: null,
    countdownBannerImage: null,
    name: null,
    type: null,

    onValidateCoupon: async () => ({
      error: 'INVALID_COUPON',
      campaign: undefined
    }),
    hasCampaignForPlanCycle: () => false,
    hasCampaign: () => false,
    onClearCoupon: () => {},
    hasValidCouponForPlanCycle: () => false,
    eligiblePlans: [],
    metadata: null,
    loaded: false
  },
  posthogFlagsLoaded: false,
  posthogFeatureFlags: undefined,
  MoisesCLI
})

export const ContextProvider = ({
  children
}: {
  children: React.ReactNode
}): ReactElement => {
  const toast = useToast()
  const { userToken, getValidToken, forceRefresh } = useFirebaseToken()
  const sidebar = useSidebar()
  const {
    user,
    loadingUserData,
    retry,
    silentRetry,
    isFetchingUserData,
    posthogFeatureFlags,
    posthogFlagsLoaded
  } = useGetUserData({ userToken, getValidToken, forceRefresh })
  const event = useEvent({ user, MoisesCLI })
  const campaign = useCampaign({ userToken })

  useEffect(() => {
    MoisesCLI.auth(userToken)
  }, [userToken])

  return (
    <ContextShell.Provider
      value={{
        user,
        userToken,
        getValidToken,
        refreshToken: forceRefresh,
        loadingUserData,
        silentRetry,
        retry,
        event,
        toast,
        sidebar,
        campaign,
        posthogFeatureFlags,
        posthogFlagsLoaded,
        isFetchingUserData,
        MoisesCLI
      }}
    >
      {children}
    </ContextShell.Provider>
  )
}
